import { BirdType, CostBreakdown, ValidationResult } from '../types';

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  return Math.round(distance * 100) / 100; // Round to 2 decimal places
};

export const validateMessage = (
  message: string,
  distance: number,
  bird: BirdType
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate message length
  if (message.length === 0) {
    errors.push('Message cannot be empty');
  } else if (message.length > bird.properties.maxMessageLength) {
    errors.push(`Message too long. ${bird.name} can only carry messages up to ${bird.properties.maxMessageLength} characters. Your message is ${message.length} characters.`);
  }

  // Distance warnings are now handled in the UI, not as validation errors

  // Add warnings for efficiency
  if (message.length > bird.properties.maxMessageLength * 0.8) {
    warnings.push(`Your message is quite long. Consider using a bird with higher capacity for better value.`);
  }

  if (distance > bird.properties.maxDistance * 0.8) {
    warnings.push(`This is near the maximum range for ${bird.name}. Consider a bird with longer range for better reliability.`);
  }

  if (bird.properties.reliability < 90) {
    warnings.push(`${bird.name} has ${bird.properties.reliability}% reliability. Consider a more reliable bird for important messages.`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

export const calculateCost = (
  message: string,
  distance: number,
  bird: BirdType
): CostBreakdown => {
  const baseCost = bird.properties.baseCost;
  const distanceCost = distance * bird.properties.costPerKm;
  
  // Calculate additional cost for characters over a base limit (e.g., first 100 chars free)
  const baseCharacterLimit = 100;
  const extraCharacters = Math.max(0, message.length - baseCharacterLimit);
  const messageCost = extraCharacters * bird.properties.costPerCharacter;

  const total = baseCost + distanceCost + messageCost;

  return {
    baseCost: Math.round(baseCost * 100) / 100,
    distanceCost: Math.round(distanceCost * 100) / 100,
    messageCost: Math.round(messageCost * 100) / 100,
    total: Math.round(total * 100) / 100,
    currency: 'USD'
  };
};

export const estimateDeliveryTime = (distance: number, bird: BirdType): number => {
  // Calculate delivery time in hours, add some buffer time for takeoff/landing
  const flightTime = distance / bird.properties.speed;
  const bufferTime = 0.5; // 30 minutes buffer
  return Math.round((flightTime + bufferTime) * 100) / 100;
};

export const formatDeliveryTime = (hours: number): string => {
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else if (hours < 24) {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (minutes === 0) {
      return `${wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
    }
    return `${wholeHours}h ${minutes}m`;
  } else {
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    if (remainingHours === 0) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
    return `${days}d ${remainingHours}h`;
  }
};

export const getBestBirdForMessage = (
  message: string,
  distance: number,
  availableBirds: BirdType[],
  prioritizeSpeed = false,
  prioritizeCost = false
): BirdType | null => {
  // Filter birds that can handle the message and distance
  const suitableBirds = availableBirds.filter(bird => {
    const validation = validateMessage(message, distance, bird);
    return validation.isValid;
  });

  if (suitableBirds.length === 0) return null;

  if (prioritizeSpeed) {
    return suitableBirds.reduce((fastest, current) => 
      current.properties.speed > fastest.properties.speed ? current : fastest
    );
  }

  if (prioritizeCost) {
    return suitableBirds.reduce((cheapest, current) => {
      const cheapestCost = calculateCost(message, distance, cheapest).total;
      const currentCost = calculateCost(message, distance, current).total;
      return currentCost < cheapestCost ? current : cheapest;
    });
  }

  // Default: balance of reliability, speed, and cost
  return suitableBirds.reduce((best, current) => {
    const bestScore = best.properties.reliability * 0.4 + best.properties.speed * 0.3 + (100 - calculateCost(message, distance, best).total) * 0.3;
    const currentScore = current.properties.reliability * 0.4 + current.properties.speed * 0.3 + (100 - calculateCost(message, distance, current).total) * 0.3;
    return currentScore > bestScore ? current : best;
  });
};
